import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import useAuth from '@/auth/useAuth';
import axiosInstance from '@/lib/axiosInstance';
import PATH_PAGE from '@/router/path';
import { Logo } from '@/components/ui/icons';
import { Button } from '@/components/ui/button';

type Status = 'loading' | 'fetching' | 'redirecting' | 'error';

// Security: Only allow VS Code URI schemes
const ALLOWED_SCHEMES = ['vscode://', 'vscode-insiders://'];

const isValidRedirectUri = (uri: string): boolean => {
  return ALLOWED_SCHEMES.some(scheme => uri.startsWith(scheme));
};

const VSCodeCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session, isLoading } = useAuth();
  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState<string | null>(null);

  const redirectUri = searchParams.get('redirect_uri');
  const clientType = searchParams.get('client_type');

  useEffect(() => {
    if (isLoading) return;

    // Validate redirect_uri
    if (!redirectUri) {
      setError('Missing redirect URI');
      setStatus('error');
      return;
    }

    if (!isValidRedirectUri(redirectUri)) {
      setError('Invalid redirect URI. Only VS Code URIs are allowed.');
      setStatus('error');
      return;
    }

    // If not authenticated, store params and redirect to sign-in
    if (!session) {
      sessionStorage.setItem('vscode_redirect_uri', redirectUri);
      if (clientType) {
        sessionStorage.setItem('vscode_client_type', clientType);
      }
      window.location.href = PATH_PAGE.auth.signIn;
      return;
    }

    // User is authenticated, fetch token and redirect
    fetchAndRedirect();
  }, [session, isLoading, redirectUri, clientType]);

  const fetchAndRedirect = async () => {
    setStatus('fetching');
    try {
      const response = await axiosInstance.get<{ token: string }>('/auth/token-cli');
      const token = response.data.token;

      if (!token) {
        throw new Error('No token received from server');
      }

      setStatus('redirecting');

      // Build callback URL with token
      const callbackUrl = new URL(redirectUri!);
      callbackUrl.searchParams.set('token', token);

      // Redirect to VS Code
      window.location.href = callbackUrl.toString();
    } catch (err: any) {
      console.error('Failed to fetch token:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to fetch authentication token');
      setStatus('error');
    }
  };

  const handleRetry = () => {
    setError(null);
    setStatus('loading');
    fetchAndRedirect();
  };

  const handleCancel = () => {
    // Redirect to VS Code with error
    if (redirectUri) {
      const callbackUrl = new URL(redirectUri);
      callbackUrl.searchParams.set('error', 'User cancelled');
      window.location.href = callbackUrl.toString();
    } else {
      navigate('/');
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="bg-gray-50">
      <div className="relative isolate flex h-screen min-h-screen flex-col items-center justify-center bg-gray-50 text-gray-900">
        {/* Background pattern */}
        <svg
          aria-hidden="true"
          className="absolute inset-0 -z-10 h-full w-full stroke-gray-200 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
        >
          <defs>
            <pattern
              x="50%"
              y={-1}
              id="vscode-auth-pattern"
              width={200}
              height={200}
              patternUnits="userSpaceOnUse"
            >
              <path d="M.5 200V.5H200" fill="none" />
            </pattern>
          </defs>
          <rect
            fill="url(#vscode-auth-pattern)"
            width="100%"
            height="100%"
            strokeWidth={0}
          />
        </svg>

        <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
          <div className="flex flex-col items-center">
            <Logo className="mb-4 h-12 w-12" />

            {status === 'loading' && (
              <>
                <h1 className="mb-2 text-xl font-semibold">Checking Authentication</h1>
                <p className="text-center text-sm text-gray-500">
                  Please wait while we verify your credentials...
                </p>
                <div className="mt-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-900" />
              </>
            )}

            {status === 'fetching' && (
              <>
                <h1 className="mb-2 text-xl font-semibold">Fetching Token</h1>
                <p className="text-center text-sm text-gray-500">
                  Retrieving your authentication token...
                </p>
                <div className="mt-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-900" />
              </>
            )}

            {status === 'redirecting' && (
              <>
                <h1 className="mb-2 text-xl font-semibold">Redirecting to VS Code</h1>
                <p className="text-center text-sm text-gray-500">
                  You should be redirected automatically. If not, click the button below.
                </p>
                <div className="mt-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-900" />
                <a
                  href={redirectUri || '#'}
                  className="mt-4 text-sm text-blue-600 hover:underline"
                >
                  Click here if not redirected
                </a>
                <p className="mt-4 text-center text-xs text-gray-400">
                  You can close this window after VS Code opens.
                </p>
              </>
            )}

            {status === 'error' && (
              <>
                <h1 className="mb-2 text-xl font-semibold text-red-600">Authentication Error</h1>
                <p className="mb-4 text-center text-sm text-gray-600">{error}</p>
                <div className="flex gap-2">
                  {redirectUri && isValidRedirectUri(redirectUri) && (
                    <Button onClick={handleRetry} variant="default">
                      Retry
                    </Button>
                  )}
                  <Button onClick={handleCancel} variant="outline">
                    Cancel
                  </Button>
                  <Button onClick={handleGoHome} variant="ghost">
                    Go to Dashboard
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-gray-400">
          Connecting Float16 VS Code Extension
        </p>
      </div>
    </div>
  );
};

export default VSCodeCallbackPage;
